import { SearchBar } from 'common/components/Forms/Inputs/SearchBar'
import Select from 'common/components/Forms/Inputs/Select'
import { LoadingBox } from 'common/components/States/Loading/LoadingBox'
import { NoDataBox } from 'common/components/States/NoData/NoDataBox'
import { useGet, useGetPaginated } from 'common/hooks/useApi'
import { pagesPathsEnum } from 'common/router/router.enum'
import { debounce } from 'lodash'
import { useRouter } from 'next/router'
import { DataView } from 'primereact/dataview'
import { DropdownChangeParams } from 'primereact/dropdown'
import { TabPanel, TabView, TabViewTabChangeParams } from 'primereact/tabview'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { userCategoryEnum } from 'services/auth/auth.enums'
import { useUserPublic } from 'services/auth/hooks/useUser'
import { ApiPathsWiki } from 'services/community/wiki.api'
import { ICommunityWiki } from 'services/community/wiki.types'
import { ICountryFilter, IPageFilters } from 'services/filters/filters.types'
import styled from 'styled-components'
import { CommunityAdminAddWikiDialog } from './CommunityAdminAddWikiDialog'
import CommunityWikiListItem from './CommunityWikiListItem'

const CommunityWikiList = () => {
  const router = useRouter()
  const [searchParams, setSearchParams] = useState('')
  const { data: user } = useUserPublic()

  const { i18n } = useTranslation()

  const [community, setCommunity] = useState({
    id: '',
    name: 'Worldwide',
    phonePrefix: '',
    countryCode: '',
    label: 'Worldwide',
  })
  const { data: countriesWithWiki = [], isLoading: isLoadingCountries } = useGet<ICountryFilter[]>(
    ApiPathsWiki.countriesWithWikiList,
  )
  const [lazyParams, setLazyParams] = useState({
    first: 1,
    rows: 20,
    page: 0,
    search: '',
  })
  const { tab } = router.query as { tab?: 'news' | 'question' | 'poll' | 'poll-ship' }
  const categoryList = ['', 'news', 'question', 'poll', 'poll-ship']

  useEffect(() => {
    if (countriesWithWiki.length) {
      const defaultCountryByLanguage = countriesWithWiki.find(
        (country) => country.countryCode.toLowerCase() === i18n.language,
      )
      if (defaultCountryByLanguage) {
        setCommunity({
          id: defaultCountryByLanguage.id,
          name: defaultCountryByLanguage.name,
          phonePrefix: defaultCountryByLanguage.phonePrefix,
          countryCode: defaultCountryByLanguage.countryCode,
          label: defaultCountryByLanguage.name,
        })
      }
    }
  }, [countriesWithWiki, i18n.language])

  const countryOptions = useMemo(() => {
    const result = countriesWithWiki
      .map((country) => {
        if (country.name !== 'Netherlands Antilles') {
          return {
            id: country.id,
            name: country.name,
            phonePrefix: country.phonePrefix,
            countryCode: country.countryCode,
            label: country.name,
          }
        } else {
          return {
            id: 'delete',
            name: 'delete',
            phonePrefix: 'delete',
            countryCode: 'delete',
          }
        }
      })
      .filter((country) => country.name !== 'delete')

    result.unshift({
      id: '',
      name: 'Worldwide',
      phonePrefix: '',
      countryCode: '',
      label: 'Worldwide',
    })

    return result
  }, [countriesWithWiki])

  const pageFilters: IPageFilters = useMemo(
    () => ({
      page: lazyParams.page! + 1 || 1,
      limit: lazyParams.rows || 20,
      search: lazyParams.search ? lazyParams.search : undefined,
      communityTag: community?.id?.length > 1 ? community.name : 'Worldwide',
      category: tab ? tab : undefined,
    }),
    [lazyParams, community, tab],
  )

  const onChangeParams = (event: any) => {
    setLazyParams((prevValues) => {
      return { ...prevValues, ...event }
    })
  }

  const sendRequest = (e: React.ChangeEvent<HTMLInputElement>) => {
    const oldLazyParams = { ...lazyParams }
    setLazyParams({ ...oldLazyParams, search: e.target.value })
  }

  const debouncedSendRequest = useMemo(() => {
    return debounce(sendRequest, 1000)
  }, [sendRequest])

  const onChangeInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchParams(e.target.value)
      debouncedSendRequest(e)
    },
    [setSearchParams],
  )

  const onChangeTab = (e: TabViewTabChangeParams) => {
    router.push(
      e.index !== 0 ? `${pagesPathsEnum.communityWiki}?tab=${categoryList[e.index]}` : pagesPathsEnum.communityWiki,
    )
  }

  const queryPathKey = useMemo(() => ApiPathsWiki.publicWiki(pageFilters), [pageFilters])
  const { data, metaData, isLoading: isLoadingWikis, error, mutate } = useGetPaginated<ICommunityWiki[]>(queryPathKey)

  if (error) {
    return <div className="mx-5">{error}</div>
  }

  const isValidatorOrAdmin =
    user?.category && [(userCategoryEnum.ADMIN, userCategoryEnum.VALIDATOR)].includes(user?.category)
  const isLoading = isLoadingWikis || isLoadingCountries
  return (
    <Div className="py-4 px-2 md:px-6 p-relative">
      <div className="wiki-header-container">
        <div className="title">Community:</div>
        <Select
          filter
          label=""
          showFilterClear
          id="country"
          placeholder="Select"
          value={community}
          options={countryOptions}
          onChange={(e: DropdownChangeParams) => {
            setCommunity(e.target.value)
          }}
        />
      </div>
      <TabView activeIndex={tab ? categoryList.indexOf(tab) : 0} onTabChange={(e) => onChangeTab(e)}>
        <TabPanel header="All"></TabPanel>
        <TabPanel header="News"></TabPanel>
        <TabPanel header="Questions"></TabPanel>
        <TabPanel header="Company reviews"></TabPanel>
        <TabPanel header="Ship reviews"></TabPanel>
      </TabView>

      {isValidatorOrAdmin && (
        <div className="py-3 flex justify-content-center w-full">
          <CommunityAdminAddWikiDialog mutate={mutate} />
        </div>
      )}
      <div className="wiki-search-container">
        <SearchBar placeholder="Search" value={searchParams} setValue={onChangeInput} className="w-full search-input" />
      </div>
      <div className="wiki-list-container">
        {isLoading && <LoadingBox size={56} minHeight="45vh" />}
        {metaData?.totalItems === 0 && <NoDataBox minHeight="45vh" />}
        {metaData?.totalItems !== 0 && !isLoading && (
          <DataView
            value={data}
            itemTemplate={(item) => <CommunityWikiListItem item={item} refetch={mutate} />}
            paginator
            rows={lazyParams?.rows || 20}
            first={lazyParams?.first}
            totalRecords={metaData?.totalItems}
            lazy
            loading={isLoading}
            onPage={onChangeParams}
            dataKey="id"
            emptyMessage="No data."
          />
        )}
      </div>
    </Div>
  )
}

const Div = styled.div`
  max-width: 1352px;
  margin-inline: auto;

  .wiki-header-container {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 22px;
    margin-bottom: 10px;
    .title {
      font-weight: 500;
      font-size: 22px;
    }

    .p-dropdown {
      border: none;
      font-size: 22px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.black};
    }

    .p-placeholder {
      font-size: 22px;
    }

    .p-dropdown-label {
      font-size: 22px;
      color: ${({ theme }) => theme.colors.black};
    }
  }

  .wiki-search-container {
    width: 100%;

    .search-input {
      border-radius: 8px;
    }
  }

  .wiki-list-container {
    position: relative;
    margin-top: 10px;
    min-height: 70vh;

    .grid-nogutter {
      > .dataview-item:nth-child(odd) {
        background-color: ${({ theme }) => theme.colors.secondary_10};
      }
    }

    .p-paginator-page.p-highlight {
      background: ${({ theme }) => theme.colors.secondary_25};
      border-color: ${({ theme }) => theme.colors.secondary_25};
    }

    .p-paginator-bottom {
      padding: 0.5rem 0;
    }
    .p-paginator {
      border: none;
    }
  }
`

export default CommunityWikiList
